// Angular:
import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';

// Dependencies:
import { isBool, Value } from '@trademe/ensure';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StepIndicatorItemComponent } from '../step-indicator-item/step-indicator-item.component';
import { IStepIndicatorComponent } from './istep-indicator.component';

@Component({
    selector: 'tm-step-indicator',
    templateUrl: 'step-indicator.component.html',
    styleUrls: ['../step-indicator.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class StepIndicatorComponent
    implements AfterContentInit, OnChanges, OnDestroy, IStepIndicatorComponent {
    @HostBinding('class.o-step-indicator') public stepIndicator = true;

    @Input() public selected: any | null = null;
    @Input() @Value(isBool) public compact: boolean;
    @Input() @Value(isBool) public showTitles = true;

    @Output() public selectedChange = new EventEmitter<any | null>();
    @Output() beforeStepChange = new EventEmitter<{
        cb: (arg?: any) => void;
        arg?: any;
        isBack?: boolean;
    }>();

    public selectedItem: StepIndicatorItemComponent | null;
    public nextItem: StepIndicatorItemComponent | null;
    public prevItem: StepIndicatorItemComponent | null;
    public currentStepNumber: number;
    // eslint-disable-next-line @angular-eslint/no-forward-ref
    @ContentChildren(forwardRef(() => StepIndicatorItemComponent), {
        descendants: true
    })
    public items: QueryList<StepIndicatorItemComponent>;

    private _destroyed$ = new Subject();

    constructor(private _changeDetector: ChangeDetectorRef) {}

    public ngOnDestroy(): void {
        this._destroyed$.next(true);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const { selected } = changes;
        if (selected && !selected.isFirstChange()) {
            this._update();
        }
    }

    public ngAfterContentInit(): void {
        const stepsUpdated = () => {
            // Take a copy of the step list whenever the query changes. This allows us to easily
            // look up steps by their index in the list.
            this._validate();
            this._update();
        };

        this.items.changes.pipe(takeUntil(this._destroyed$)).subscribe(() => {
            stepsUpdated();
            // NOTE: QueryList change events do not trigger change detection automatically.
            this._changeDetector.detectChanges();
        });

        stepsUpdated();

        this._emitChanged();
    }

    public moveBack(): void {
        this.beforeStepChange.emit({ cb: this.navigateBack, isBack: true });
    }

    public navigateBack = (): void => {
        if (this.prevItem) {
            this.selected = this.prevItem.id;
            this._update();
            this._emitChanged();
        }
    };

    public moveForward(): void {
        this.beforeStepChange.emit({ cb: this.navigateForward });
    }

    public navigateForward = (): void => {
        if (this.nextItem) {
            this.selected = this.nextItem.id;
            this._update();
            this._emitChanged();
        }
    };

    public select(item: StepIndicatorItemComponent): void {
        const index = this.items.toArray().findIndex((x) => x.id === item.id);
        // currentStepNumber index begins at 1
        const isBack = index + 1 < this.currentStepNumber;
        this.beforeStepChange.emit({
            cb: this.performSelecting,
            arg: item,
            isBack: isBack
        });
    }

    public performSelecting = (item: StepIndicatorItemComponent): void => {
        // Verify the step item is a child of this component
        const found = this.items.find((step) => step.id === item.id);

        if (!found) {
            return;
        }

        // Verify the step is not already selected
        if (item.id === this.selected) {
            return;
        }

        this.selected = item.id;
        this._update();
        this._emitChanged();
    };

    private _validate(): void {
        if (!this.items.length) {
            // this._loggerService.logWarning(
            //     STEP_INDICATOR_WITHOUT_ITEMS.message
            // );
        }

        const existing = new Set<any>();
        this.items.forEach((step) => {
            if (existing.has(step.id)) {
                // this._loggerService.logWarning(
                //     STEP_INDICATOR_ITEMS_WITH_DUPLICATE_IDS.message
                // );
            }
            existing.add(step.id);
        });
    }

    private _update(): void {
        let selectedItem: StepIndicatorItemComponent | null = null;
        let prevItem: StepIndicatorItemComponent | null = null;
        let nextItem: StepIndicatorItemComponent | null = null;
        let currentStepNumber = 0;

        // Iterate over the step items and for each:
        //  1. Set the `active` flag.
        //  2. Locate the "prev", "next" and "selected" items.
        //      - `selectedItem` is identified by ID
        //      - The next item after we find `selectedItem` must be `nextItem`
        //      - Assume each item is `prevItem` until we find `selectedItem`
        this.items.forEach((item, index) => {
            const isSelectedStep = item.id === this.selected;
            if (isSelectedStep) {
                selectedItem = item;
                currentStepNumber = index + 1;
            } else if (selectedItem && !nextItem) {
                nextItem = item;
            } else if (!selectedItem) {
                prevItem = item;
            }
            // NOTE: Updating `active` for ALL steps
            item.active = isSelectedStep;
        });

        // No step matching the given ID?
        // Raise an error and rely on the caller to set an appropriate step ID
        if (!selectedItem) {
            // this._loggerService.logWarning(
            //     STEP_INDICATOR_NO_SELECTED_STEP(this.selected).message
            // );
        }

        // Update item selection state and select items of interest
        this.selectedItem = selectedItem;
        this.prevItem = prevItem;
        this.nextItem = nextItem;
        this.currentStepNumber = currentStepNumber;
    }

    private _emitChanged(): void {
        this.selectedChange.emit(this.selected);
    }
}

export const STEP_INDICATOR_WITHOUT_ITEMS = {
    message:
        '<tg-step-indicator> must contain at least one <tg-step-indicator-item>.',
    example: `
    <tg-step-indicator
        selected="step-1">
        <tg-step-indicator-item
            id="step-1">
        </tg-step-indicator-item>
    </tg-step-indicator>
    `
};

export const STEP_INDICATOR_ITEMS_WITH_DUPLICATE_IDS = {
    message:
        'Every <tg-step-indicator-item> must have a unique ID within the <tg-step-indicator>',
    example: `
    <tg-step-indicator
        selected="step-1">
        <tg-step-indicator-item
            id="step-1">
        </tg-step-indicator-item>
        <tg-step-indicator-item
            id="step-2">
        </tg-step-indicator-item>
        <tg-step-indicator-item
            id="step-3">
        </tg-step-indicator-item>
    </tg-step-indicator>
    `
};

export const STEP_INDICATOR_NO_SELECTED_STEP = (selectedId: any) => {
    const message =
        selectedId === null || selectedId === undefined
            ? '<tg-step-indicator> selected step ID is not set. Have you selected an initial step?'
            : `<tg-step-indicator> does not have a step with ID ${JSON.stringify(
                  selectedId
              )}`;

    return {
        message,
        example: `
     <tg-step-indicator
        selected="step-1">
        <tg-step-indicator-item
            id="step-1">
        </tg-step-indicator-item>
        ...
    </tg-step-indicator>

    or

    <tg-step-indicator
        [(selected)]="currentStep">
        <tg-step-indicator-item
            id="step-1">
        </tg-step-indicator-item>
        ...
    </tg-step-indicator>
    `
    };
};
